import { useCallback, useEffect, useState } from 'react';

/**
 * Determines whether a target element has scrolled past a threshold,
 * using scroll events + getBoundingClientRect instead of IntersectionObserver
 * to avoid false positives during layout settling after data-router navigation.
 *
 * @param {Element} target
 * @param {number} elementHeight
 * @returns {boolean}
 */

const useSticky = (target, elementHeight) => {
  const [isSticky, setIsSticky] = useState(false);

  const checkSticky = useCallback(() => {
    if (!target) return;
    const rect = target.getBoundingClientRect();
    setIsSticky(rect.bottom <= elementHeight);
  }, [target, elementHeight]);

  useEffect(() => {
    if (!target) return;

    window.addEventListener('scroll', checkSticky, { passive: true });
    window.addEventListener('resize', checkSticky, { passive: true });

    // Deferred initial check — double RAF ensures at least one paint has completed
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(checkSticky);
    });

    return () => {
      window.removeEventListener('scroll', checkSticky);
      window.removeEventListener('resize', checkSticky);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [checkSticky, target]);

  return isSticky;
};

export default useSticky;
